import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { toast } from 'sonner';
import flagsmithService, {
  clearUserIdentity,
  identifyUser,
  initFlagsmith,
  setUserTrait,
} from '../lib/flagsmith';

// Create a context for Flagsmith
interface FlagsmithContextType {
  initialized: boolean;
  error: Error | null;
  identifyUser: (userId: string) => void;
  setUserTrait: (key: string, value: string | number | boolean) => void;
  clearUserIdentity: () => void;
  flagsmith: typeof flagsmithService;
}

const FlagsmithContext = createContext<FlagsmithContextType | undefined>(
  undefined
);

// Hook to use the Flagsmith context
export function useFlagsmithContext() {
  const context = useContext(FlagsmithContext);
  if (context === undefined) {
    throw new Error(
      'useFlagsmithContext must be used within a FlagsmithProvider'
    );
  }
  return context;
}

interface FlagsmithProviderProps {
  children: ReactNode;
  fallback?: ReactNode; // Optional loading component
}

/**
 * Provider component that initializes Flagsmith at the application level
 * Wrap your application with this component to ensure Flagsmith is initialized
 * before rendering your components.
 */
export function FlagsmithProvider({
  children,
  fallback,
}: FlagsmithProviderProps) {
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        await initFlagsmith();
        if (isMounted) {
          setInitialized(true);
        }
      } catch (err) {
        console.error('Failed to initialize Flagsmith:', err);
        if (isMounted) {
          setError(
            err instanceof Error
              ? err
              : new Error('Failed to initialize Flagsmith')
          );
          // Show error toast only in development
          if (import.meta.env.DEV) {
            toast.error('Failed to initialize feature flags');
          }
          // Still set initialized to true to render the app even if Flagsmith fails
          setInitialized(true);
        }
      }
    };

    init();

    return () => {
      isMounted = false;
    };
  }, []);

  // Create the context value
  const contextValue: FlagsmithContextType = {
    initialized,
    error,
    identifyUser,
    setUserTrait,
    clearUserIdentity,
    flagsmith: flagsmithService,
  };

  // Show loading state if not initialized
  if (!initialized && fallback) {
    return <>{fallback}</>;
  }

  return (
    <FlagsmithContext.Provider value={contextValue}>
      {children}
    </FlagsmithContext.Provider>
  );
}

export default FlagsmithProvider;
